import { ref, watch } from 'vue';
import { usePreset } from '@primeuix/themes';
import { persologDefault, persologHighContrast } from '@persolog/theme';
import { applyThemeAttribute, normalizeTheme } from "@persolog/theme/themes";
import { usePreferencesStore, type ThemeType } from '../stores/preferences';

// Presets now live in @persolog/theme; re-exported so existing imports keep working.
export { persologDefault, persologHighContrast };

const currentTheme = ref<ThemeType>('default');

export function useTheme() {
    const preferencesStore = usePreferencesStore();

    const applyTheme = (newTheme: ThemeType) => {
        currentTheme.value = newTheme;

        // Update CSS class on document root for additional styling
        document.documentElement.classList.remove('theme-default', 'theme-high-contrast');
        document.documentElement.classList.add(`theme-${newTheme}`);
        // Drives the Tailwind token layer in @persolog/theme/tokens.css.
        applyThemeAttribute(normalizeTheme(newTheme));

        // Use PrimeVue's native preset switching
        const preset = newTheme === 'high-contrast' ? persologHighContrast : persologDefault;
        usePreset(preset);
    };

    const setTheme = (newTheme: ThemeType) => {
        preferencesStore.setTheme(newTheme);
    };

    const toggleTheme = () => {
        const newTheme = currentTheme.value === 'default' ? 'high-contrast' : 'default';
        setTheme(newTheme);
    };

    watch(() => preferencesStore.selectedTheme, (newTheme) => {
        applyTheme(newTheme);
    }, { immediate: true });

    return {
        theme: currentTheme,
        setTheme,
        toggleTheme,
        persologDefault,
        persologHighContrast
    };
}
